'use client';

import { clsx } from 'clsx';
import { FormikProps, useField } from 'formik';
import { isEmpty, isNil } from 'lodash';
import Script from 'next/script';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { AUSTRALIAN_STATES } from 'src/constants/constants';
import { postcodeRegex } from 'src/constants/regex.constants';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { InputFormik } from '../Input/InputFormik';
import { InputSelectFormik } from '../Input/InputSelectFormik';
import {
  boldMatchCharacters,
  extractAddressFromGoogleInput,
  validateStreetLength,
} from './FormikInputAddressAutocomplete.helpers';
import styles from './FormikInputAddressAutocomplete.module.scss';
import { usePlacesService } from './usePlacesService.hooks';

type InputAddressAutocompleteValidationConfig = {
  streetRequiredValidation?: string;
  streetTooLongValidation?: string;
  suburbRequiredValidation?: string;
  postcodeRequiredValidation?: string;
  postcodeFormatValidation?: string;
  stateValidationValidation?: string;
};

const maxCharacterValidation = (limit: number) => `Must be at most ${limit} characters.`;

export const getInputAddressAutocompleteValidation = ({
  streetRequiredValidation = 'Please add your delivery address',
  streetTooLongValidation = 'Please ensure your delivery address is properly formatted',
  suburbRequiredValidation = 'Please add your delivery suburb',
  postcodeRequiredValidation = 'Enter your postcode',
  postcodeFormatValidation = 'Please enter a valid postcode',
  stateValidationValidation = 'Enter your state',
}: InputAddressAutocompleteValidationConfig = {}) =>
  Yup.object({
    street: Yup.string()
      .required(streetRequiredValidation)
      .test('streetNameTooLong', streetTooLongValidation, validateStreetLength),
    apartment: Yup.string().max(20, maxCharacterValidation(20)),
    suburb: Yup.string()
      .required(suburbRequiredValidation)
      .max(30, maxCharacterValidation(30))
      .required(suburbRequiredValidation),
    postcode: Yup.string()
      .matches(postcodeRegex, postcodeFormatValidation)
      .max(20, maxCharacterValidation(20))
      .required(postcodeRequiredValidation),
    state: Yup.string()
      .oneOf(AUSTRALIAN_STATES)
      .max(20, maxCharacterValidation(20))
      .required(stateValidationValidation),
  });

export const OptionalInputAddressAutocompleteValidation = Yup.object({
  street: Yup.string(),
  apartment: Yup.string(),
  suburb: Yup.string(),
  postcode: Yup.string(),
  state: Yup.string(),
});

const defaultAddressValidation = getInputAddressAutocompleteValidation();

export type InputAddressAutocompleteAddress = Omit<
  ReturnType<typeof defaultAddressValidation.validateSync>,
  'state'
> & { state: any };

export const defaultValueInputAddressAutocomplete: Prettify<InputAddressAutocompleteAddress> = {
  street: '',
  apartment: '',
  suburb: '',
  postcode: '',
  state: '' as any,
};

type FormikInputAddressAutocompleteProps = {
  name: string;
  helperText?: ReactNode;
};

export const FormikInputAddressAutocomplete: FC<FormikInputAddressAutocompleteProps & FormikProps<any>> = ({
  name,
  helperText,
  setFieldValue,
  validateField,
  values,
}) => {
  const [_field, _meta] = useField(`${name}.street`);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const [isGoogleServiceReady, setGoogleServiceReady] = useState(false);

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading, resetPlacePredictions } =
    usePlacesService({
      enable: isGoogleServiceReady,
    });

  const onPlaceSelect = (_e: any, newValue: google.maps.places.AutocompletePrediction | string | null) => {
    setIsPlaceSelected(true);
    setIsDropdownOpen(false);
    if (!newValue || typeof newValue === 'string') return;
    resetAddress();
    // Fetch place detail
    placesService?.getDetails({ placeId: newValue.place_id, fields: ['address_components'] }, (placeDetail) => {
      const { street, apartment, suburb, postcode, state } = extractAddressFromGoogleInput(
        placeDetail,
        newValue.description,
      );
      fillAddress({ street, apartment, suburb, postcode, state });
    });
  };

  const resetAddress = () => {
    setFieldValue(`${name}.apartment`, '');
    setFieldValue(`${name}.suburb`, '');
    setFieldValue(`${name}.postcode`, '');
    setFieldValue(`${name}.state`, undefined);
  };

  useEffect(() => {
    if (isPlaceSelected) revalidateField();
  }, [values[name]]);

  const revalidateField = () => {
    validateField(`${name}.suburb`);
    validateField(`${name}.postcode`);
    validateField(`${name}.state`);
    validateField(`${name}.street`);
  };

  const fillAddress = ({
    street,
    apartment,
    suburb,
    postcode,
    state,
  }: {
    apartment?: string;
    street?: string;
    suburb?: string;
    postcode?: string;
    state?: string;
  }) => {
    if (!isNil(street)) {
      setFieldValue(`${name}.street`, street, false);
    }
    if (!isNil(apartment)) {
      setFieldValue(`${name}.apartment`, apartment, false);
    }
    if (!isNil(suburb)) {
      setFieldValue(`${name}.suburb`, suburb, false);
    }
    if (!isNil(postcode)) {
      setFieldValue(`${name}.postcode`, postcode, false);
    }
    if (!isNil(state)) {
      setFieldValue(`${name}.state`, state, false);
    }
  };

  const onStreetInputChange = (_e: React.ChangeEvent<{}>, inputValue: string) => {
    if (inputValue) {
      getPlacePredictions({
        input: inputValue,
        componentRestrictions: { country: ['au'] },
        types: ['address'],
      });
      setIsDropdownOpen(true);
    } else {
      resetPlacePredictions();
      fillAddress({ street: '', apartment: '', suburb: '', postcode: '', state: '' });
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if ((window as any).google?.maps?.places) {
      setGoogleServiceReady(true);
    }
  }, []);

  const onOpenChatWidget = () => {
    if (typeof window !== 'undefined' && typeof window.LiveChatWidget !== 'undefined' && !!window.LiveChatWidget)
      window.LiveChatWidget.call('maximize');

    pushToDataLayer({
      event: `checkout_click`,
      value: `license_contact_carma`,
    });
  };

  const showHelperText = !!values[name]?.state && values[name]?.state !== 'NSW' && !isEmpty(helperText);

  return (
    <>
      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GG_MAP_KEY}&libraries=places`}
        onLoad={() => {
          setGoogleServiceReady(true);
        }}
        strategy="afterInteractive"
      />
      <div
        className={clsx(styles.formWrapper, {
          [styles.withHelperText]: showHelperText,
        })}
      >
        <div className={styles.autocompleteContainer}>
          <InputFormik
            id={`${name}.street`}
            autoComplete="street-address"
            type="text"
            name={`${name}.street`}
            data-testid="street-address"
            label="Street address"
            className={styles.locationInput}
            onChangeCapture={(e) => {
              onStreetInputChange(e, e.currentTarget.value);
            }}
          />
          {isDropdownOpen && placePredictions.length > 0 && (
            <div className={styles.dropdownRoot}>
              {isPlacePredictionsLoading ? (
                <div>Loading...</div>
              ) : (
                placePredictions.map((option) => (
                  <button
                    className={styles.itemOption}
                    data-testid="MakeModelAutocomplete-option"
                    key={option.place_id}
                    onClick={(e) => onPlaceSelect(e, option)}
                  >
                    <span className={clsx('c-fs-body1')} data-testid="street-address">
                      {boldMatchCharacters(option.description, option.matched_substrings)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <InputFormik
          id={`${name}.apartment`}
          name={`${name}.apartment`}
          label="Apartment, suite, etc (optional)"
          type="text"
          className={styles.appartment}
          data-testid="apartment"
        />
        <div className={styles.row}>
          <InputFormik
            id={`${name}.suburb`}
            name={`${name}.suburb`}
            label="Suburb"
            type="text"
            autoComplete="address-level2"
            className={styles.suburb}
            data-testid="suburb"
          />

          <InputFormik
            id={`${name}.postcode`}
            name={`${name}.postcode`}
            label="Postcode"
            type="text"
            autoComplete="postal-code"
            inputMode="numeric"
            maxLength={4}
            className={styles.postcode}
            data-testid="postcode"
          />

          <InputSelectFormik
            id={`${name}.state`}
            name={`${name}.state`}
            autoComplete="address-level1"
            label="State"
            allowEmpty
            options={AUSTRALIAN_STATES.map((state) => ({ value: state, label: state }))}
            className={styles.state}
            data-testid="state"
          />
        </div>
        {showHelperText && (
          <div>
            {helperText}
            <span>{`If you have any questions, `}</span>
            <button type="button" className={styles.contactButton} onClick={onOpenChatWidget}>
              contact us.
            </button>
          </div>
        )}
      </div>
    </>
  );
};
